// @flow strict
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu/Menu';
import * as React from 'react';
// $FlowFixMe[nonstrict-import]
import * as reduxApi from 'src/utils/redux-api-v2';
import {useApi} from 'src/hooks/useApi';
import {debounce} from 'lodash';
import {useDispatch} from 'react-redux';
import {Typeahead} from '@spaced-out/ui-design-system/lib/components/Typeahead';
import {Chip} from '@spaced-out/ui-design-system/lib/components/Chip';

import css from './typeahead.css';


const USER_SEARCH_URL =
  'users?nameOrUsername={query}&pageSize=10&pageNumber=0&teams=&candidateId=0';

export type TypeAheadProps = {
  onSelectOption: (option: Array<MenuOption>) => void,
  label: string | React.Node,
  isApiCall?: boolean,
  apiUrl?: string,
  required?: boolean,
  placeholder?: string,
  typeAheadOptions?: Array<MenuOption>,
  isUserSearch?: boolean,
  isSingleSelect?: boolean,
  selectedOption?: Array<MenuOption>,
  disabled?: boolean,
  showChips?: boolean,
  error?: boolean,
  errorText?: string,
  nonDismissibleChips?: boolean,
  doNotShowOptionsIfNoResults?: boolean,
  sourceTrailInfo?: Array<{
    referrerId: number,
  }>,
};

export const TypeAheadSelector = ({
  label,
  placeholder = '',
  typeAheadOptions = [],
  apiUrl = '',
  onSelectOption,
  required,
  isApiCall = false,
  isUserSearch = false,
  isSingleSelect = false,
  selectedOption = [],
  disabled = false,
  showChips = true,
  error = false,
  errorText = '',
  nonDismissibleChips = false,
  doNotShowOptionsIfNoResults = false,
  sourceTrailInfo = [],
}: TypeAheadProps): React.Node => {
  const dispatch = useDispatch();
  const [fetchedItemList, setFetchedItemList] =
    React.useState(typeAheadOptions);
  const [selectedListItems, setSelectedListItems] =
    React.useState(selectedOption);
  const [isUseLoading, setIsUseLoading] = React.useState(false);
  const [searchedQuery, setSearchedQuery] = React.useState('');

  const fetchListItems = async (query) => {
    const apiEndpoint = isUserSearch
      ? USER_SEARCH_URL.replace('{query}', query)
      : `${apiUrl}${query}`;
    const response = await dispatch(
      reduxApi.get(apiEndpoint, {}, {apiPath: '/trmapi/api/'}),
    );
    return response?.content || [];
  };

  const searchListItems = React.useCallback(
    debounce(async (query) => {
      setIsUseLoading(true);
      const usersList = await fetchListItems(query);
      setFetchedItemList(
        usersList.length > 0
          ? mapFetchedItems(usersList)
          : doNotShowOptionsIfNoResults
          ? []
          : [{key: query, label: query}],
      );
      setIsUseLoading(false);
    }, 300),
    [dispatch, apiUrl, sourceTrailInfo],
  );

  const handleSearchItem = (evt) => {
    const query = evt.target.value;
    setSearchedQuery(query);

    if (apiUrl || isUserSearch) {
      searchListItems(query);
    } else {
      const filteredItems = typeAheadOptions.filter((item) =>
        item.label?.toLowerCase().includes(query.toLowerCase()),
      );
      setFetchedItemList(
        filteredItems.length
          ? filteredItems
          : doNotShowOptionsIfNoResults
          ? []
          : [{key: query, label: query}],
      );
    }
  };

  const mapFetchedItems = (items) => {
    return items.map((item) => ({
      key: item.id,
      label: isUserSearch ? item.prettyName : item.label,
      secondaryLabel:
        sourceTrailInfo &&
        sourceTrailInfo.some((candidate) => candidate.referrerId === item.id)
          ? 'Referrer'
          : '',
    }));
  };

  const handleUpdateSelectedItem = (selectedUser) => {
    if (isSingleSelect) {
      setSelectedListItems([selectedUser]);
      onSelectOption([selectedUser]);
    } else if (
      !selectedListItems.some((item) => item.key === selectedUser.key)
    ) {
      const updatedList = [...selectedListItems, selectedUser];
      setSelectedListItems(updatedList);
      onSelectOption(updatedList);
    }
    setSearchedQuery('');
  };

  const handleDismiss = (key) => {
    const updatedList = selectedListItems.filter((item) => item.key !== key);
    setSelectedListItems(updatedList);
    onSelectOption(updatedList);
  };

  const fetchUserDetails = async (userIds) => {
    const userData = await dispatch(
      reduxApi.post(
        `api/get-users`,
        {
          userIds,
        },
        {},
        {
          apiPath: '/trmapi/',
        },
      ),
    );
    return userData;
  };

  React.useEffect(() => {
    const options = [];
    const userIds = isUserSearch
      ? (selectedOption || []).map((item) => item.key)
      : [];
    if (userIds.length > 0) {
      fetchUserDetails(userIds).then((userList) => {
        if (userList.length > 0) {
          userList.forEach((user) => {
            options.push({
              key: user.id.toString(),
              label: user.prettyName,
            });
          });
          setSelectedListItems(options);
        } else {
          setSelectedListItems(selectedOption);
        }
      });
    } else {
      setSelectedListItems(selectedOption);
    }
  }, [selectedOption]);

  React.useEffect(() => {
    setFetchedItemList(typeAheadOptions);
    setSearchedQuery('');
  }, [typeAheadOptions]);

  React.useEffect(() => {
    if (searchedQuery === '') {
      setFetchedItemList(typeAheadOptions);
    }
  }, [searchedQuery]);

  return (
    <div className={css.column}>
      <Typeahead
        label={label}
        placeholder={placeholder}
        menu={{
          options: fetchedItemList,
          selectedKeys: selectedListItems.map((item) =>
            // $FlowFixMe[incompatible-type]
            isUserSearch ? parseInt(item.key, 10) : item.key,
          ),
        }}
        menuOpenOffset={0}
        required={required}
        onSearch={handleSearchItem}
        onSelect={handleUpdateSelectedItem}
        isLoading={isUseLoading}
        typeaheadInputText={searchedQuery}
        onClear={() => setSearchedQuery('')}
        disabled={disabled}
        error={error}
        errorText={errorText}
      />
      {showChips ? (
        <div className={css.selectedChipContainer}>
          {selectedListItems.map((listItem) => (
            <Chip
              key={listItem.key}
              semantic="information"
              dismissable={!disabled && !nonDismissibleChips}
              onDismiss={() => handleDismiss(listItem.key)}
            >
              {listItem.label || ''}
            </Chip>
          ))}
        </div>
      ) : null}
    </div>
  );
};
